import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtGBP, fmtDate, statusTone } from "@/lib/format";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { ChevronLeft } from "lucide-react";
import FollowButton from "@/components/FollowButton";

const PIE = ["#D4AF37", "#0EA5E9", "#10B981", "#8B5CF6", "#F59E0B", "#EF4444", "#64748b"];

export default function ArtistDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { data: a } = useQuery({
    queryKey: ["artist", cid],
    queryFn: async () => (await api.get(`/artists/${cid}`)).data,
  });
  if (!a) return <div className="al-card p-16 text-center text-slate-500">Loading…</div>;

  const chart = a.price_history.map((p) => ({
    date: fmtDate(p.sale_date), price: p.price_gbp,
    estLow: p.estimate_low_gbp, estHigh: p.estimate_high_gbp,
  }));
  const pie = Object.entries(a.medium_breakdown).map(([name, value]) => ({ name, value }));

  return (
    <div>
      <button data-testid="artist-back-button" onClick={() => navigate("/artists")} className="al-btn-ghost mb-5"><ChevronLeft className="w-4 h-4" /> All artists</button>

      <div className="al-card p-6 flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400/90">Artist Profile</p>
          <h1 className="font-serif text-4xl font-semibold tracking-tight">{a.artist}</h1>
          {a.aliases?.length > 0 && (
            <p className="mt-2 text-xs text-slate-500">Source names: {a.aliases.map((x) => x.source_specific_name).join(" · ")}</p>
          )}
          <div className="mt-3"><FollowButton cid={a.artist_canonical_id} name={a.artist} size="lg" /></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div className="min-w-0"><p className="mono-num text-xl sm:text-2xl text-slate-100 truncate">{a.total_lots}</p><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Lots</p></div>
          <div className="min-w-0"><p className="mono-num text-xl sm:text-2xl text-emerald-400 truncate">{a.sold_count}</p><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Sold</p></div>
          <div className="min-w-0"><p className="mono-num text-xl sm:text-2xl text-cyan-400 truncate">{a.clearance_rate}%</p><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Clearance</p></div>
          <div className="min-w-0"><p className="mono-num text-xl sm:text-2xl text-amber-400 truncate">{fmtGBP(a.turnover_gbp)}</p><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Turnover</p></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <div className="al-card p-5 lg:col-span-2">
          <p className="al-label">Historical Sold Prices (GBP) vs Estimate Band</p>
          {chart.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chart} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => "£" + (v / 1000).toFixed(0) + "k"} width={55} />
                <Tooltip contentStyle={{ background: "#121824", border: "1px solid #334155", borderRadius: 8 }} formatter={(v) => fmtGBP(v)} />
                <Line type="monotone" dataKey="estHigh" stroke="#334155" strokeDasharray="4 4" dot={false} name="Est high" />
                <Line type="monotone" dataKey="estLow" stroke="#334155" strokeDasharray="4 4" dot={false} name="Est low" />
                <Line type="monotone" dataKey="price" stroke="#D4AF37" strokeWidth={2.5} dot={{ fill: "#D4AF37", r: 4 }} name="Hammer (GBP)" />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-500 py-20 text-center">No sold lots to chart.</p>}
        </div>
        <div className="al-card p-5">
          <p className="al-label">Category Breakdown</p>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} innerRadius={45}>
                {pie.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              <Tooltip contentStyle={{ background: "#121824", border: "1px solid #334155", borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="al-card mt-5 overflow-hidden">
        <p className="al-label p-4 pb-2">Top Sales</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-y border-slate-800 bg-[#0E131F] text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <th className="px-4 py-2">Title</th><th className="px-4 py-2">House</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Status</th><th className="px-4 py-2 text-right">GBP</th>
            </tr></thead>
            <tbody>
              {a.top_sales.map((l) => (
                <tr key={l.lot_id} className="border-b border-slate-800/50">
                  <td className="px-4 py-3 font-serif text-amber-400">{l.title}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{l.auction_house} · {l.sale_location}</td>
                  <td className="px-4 py-3 mono-num text-xs text-slate-400">{fmtDate(l.sale_date)}</td>
                  <td className="px-4 py-3"><span className={`al-chip ${statusTone[l.status]}`}>{l.status}</span></td>
                  <td className="px-4 py-3 mono-num text-right text-amber-400 font-semibold">{fmtGBP(l.result_price_gbp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
